import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
} from "@tanstack/react-table";
import TablePagination from "./pagination";
import MyLoader from "../loader";
import cn from "../../lib/utils/class-name";
import { motion } from "framer-motion";
import { Text } from "rizzui/typography";

const DataTable = ({
  data,
  columns,
  isLoading,
  error,
  setPage,
  pageSize,
  meta,
  billing = false,
  defaultSort = null as { id: string; desc: boolean } | null,
  isDashboardEmpty = false,
}) => {
  // Memoize data to trigger re-renders properly
  const memoizedData = useMemo(() => data || [], [data]);
  const [sorting, setSorting] = useState(() =>
    defaultSort ? [{ id: defaultSort.id, desc: defaultSort.desc }] : []
  );

  const table = useReactTable({
    data: memoizedData,
    columns,
    meta: meta,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) return <MyLoader />;
  if (error)
    return (
      <p className="font-500 font-switzer text-[14px] text-center text-primary-dark">
        Error fetching data
      </p>
    );
  if (!isDashboardEmpty && data.length < 1)
    return (
      <div className="my-auto h-full flex items-center justify-center">
        <p className="font-500 font-switzer text-[14px] text-center text-primary-dark">
          No data
        </p>
      </div>
    );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div
        className={cn(
          "border-[.5px] border-b-0 rounded-[8px] overflow-auto",
          billing ? "!border-0" : "!border-muted"
        )}
      >
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr
              className={`active:bg-inherit ${billing ? "h-fit" : "h-[56px]"}`}
            >
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`first:pl-[24px] last:pr-[24px] px-[12px] border-b border-muted text-left font-[500] font-switzer text-[12px] leading-[18px] text-[#191919] dark:text-[#FFFFFF] ${
                      billing ? "!p-0 !py-[6px] !border-0" : "h-[48px] "
                    }`}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {isDashboardEmpty ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center border-b-[.5px] border-b-muted"
                >
                  <div className="flex items-center justify-center h-[463px]">
                    <div className="flex w-[171px] flex-col items-start justify-center gap-[8px]">
                      <Text className="font-ibmPlexSerif font-[600] text-[18px] text-primary-dark">
                        No calls yet
                      </Text>
                      <Text className="font-switzer font-[400] text-[14px] text-primary-dark">
                        Your calls will appear here
                      </Text>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => meta(row)}
                  className={`${
                    billing
                      ? "h-fit"
                      : "h-[60px] cursor-pointer hover:bg-[#F5F5F5] dark:hover:bg-[#262626]"
                  }`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        width: cell.column.getSize(),
                      }}
                      className={`first:pl-[24px] last:pr-[24px] px-[12px] border-b border-muted font-[400] font-switzer text-[12px] text-elipsis leading-[15.84px] text-[#191919] dark:text-[#FFFFFF] ${
                        billing ? "!p-0 !py-[6px] !border-0" : ""
                      }`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pageSize! > 0 && (
        <TablePagination tableLength={pageSize} setPage={setPage} />
      )}
    </motion.div>
  );
};

export default DataTable;
